import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { FOOTER } from "@/content/landing";
import { SemaLogo } from "@/components/marketing/SemaLogo";
import api from "@/lib/api";

function NewsletterBlock() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [message, setMessage] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    try {
      const { data } = await api.post("/newsletter", { email: email.trim(), source: "footer" });
      setState("done");
      setMessage(data.already_subscribed ? "You're already on the list." : "You're in. One essay a month — that's it.");
    } catch (err) {
      setState("error");
      setMessage(err.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  return (
    <div
      data-testid="footer-newsletter"
      className="mt-14 rounded-2xl border border-border/60 bg-card/40 p-8 backdrop-blur sm:flex sm:items-center sm:justify-between sm:gap-10"
    >
      <div className="max-w-md">
        <h4 className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">The governed data letter</h4>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          One governed-data essay per month. No product spam, unsubscribe anytime.
        </p>
      </div>
      {state === "done" ? (
        <p data-testid="footer-newsletter-success" className="mt-5 inline-flex items-center gap-2 text-sm text-accent sm:mt-0">
          <MailCheck className="h-4 w-4" /> {message}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="mt-5 w-full max-w-md sm:mt-0">
          <div className="flex gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="footer-newsletter-email"
              className="h-11 w-full rounded-full border border-border/70 bg-background/60 px-5 text-sm outline-none transition-colors focus:border-accent"
            />
            <button
              type="submit"
              disabled={state === "loading"}
              data-testid="footer-newsletter-submit"
              className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-accent px-5 font-mono text-[11px] uppercase tracking-[0.16em] text-accent-foreground transition-colors hover:bg-accent/90 disabled:opacity-60"
            >
              {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
            </button>
          </div>
          {state === "error" && (
            <p data-testid="footer-newsletter-error" className="mt-2 text-xs text-destructive">{message}</p>
          )}
        </form>
      )}
    </div>
  );
}

export function Footer() {
  return (
    <footer
      data-testid="landing-footer"
      className="relative w-full border-t border-border/60"
    >
      <div className="mx-auto w-full max-w-6xl px-6 py-16">
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:grid-cols-7">
          <div className="col-span-2">
            <Link
              to="/"
              data-testid="landing-footer-brand"
              className="inline-flex items-center"
            >
              <SemaLogo to={null} imgClassName="h-9" testid="landing-footer-logo" />
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {FOOTER.tagline}
            </p>
            <p
              data-testid="landing-footer-compliance"
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
            >
              {FOOTER.compliance}
            </p>
          </div>

          {FOOTER.columns.map((col) => (
            <div key={col.title}>
              <h4 className="font-mono text-[11px] uppercase tracking-[0.22em] text-foreground">
                {col.title}
              </h4>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.href.startsWith("/") && !l.href.includes("#") ? (
                      <Link
                        to={l.href}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <NewsletterBlock />

        <div className="mt-14 flex flex-col gap-4 border-t border-border/60 pt-8 sm:flex-row sm:items-end sm:justify-between">
          <p
            data-testid="landing-footer-copy"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
          >
            {FOOTER.copyright}
          </p>
          <div
            aria-hidden="true"
            className="select-none font-mono text-[80px] font-bold leading-none tracking-tight text-foreground/5 sm:text-[120px]"
          >
            sema
          </div>
        </div>
      </div>
    </footer>
  );
}
